package fr.frux.core.maps

import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.waitForUpOrCancellation
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/** Zoom de repli d'un aperçu isolé : pas de contexte d'échelle à deviner (une seule position). */
private const val DEFAULT_PREVIEW_ZOOM = 16f

private val DEFAULT_PIN_SIZE = 40.dp

/**
 * Aperçu de carte non interactif, centré sur [location] avec une épingle fixe.
 *
 * Même chemin de rendu que la mini-carte de choix de position (assistant d'ajout, #147) :
 * [FruxMap] avec `showOrnaments = false` (attribution redessinée en Compose, cf. [FruxMap]) et une
 * épingle superposée qui vise le centre. Ce composant en diffère sur un point volontaire : les
 * gestes sont neutralisés — un aperçu en lecture seule ne doit jamais voler le scroll de l'écran
 * qui le contient (contrairement au picker d'AddTree, qui reste pan-able par conception). Le tap
 * reste transmis via [onClick] : c'est la seule interaction qu'un aperçu autorise (ouvrir la carte
 * plein cadre).
 *
 * Neutralisation en passe [PointerEventPass.Initial] : c'est la passe qui traverse l'arbre de
 * dehors en dedans, donc celle qui passe avant le `pointerInput` que la carte installe elle-même
 * (depuis maplibre-compose 0.15, les gestes de la carte sont des gestes Compose comme les autres).
 * Consommer plus tard, en passe Main, laisserait passer un pan déjà entamé sous le doigt.
 *
 * Desktop/placeholder (ADR 0007) : [FruxMap] y délègue déjà à un rendu de repli honnête
 * (`MapPlaceholder`) — ce composant ne duplique pas ce texte, il réutilise le même chemin que le
 * reste de l'application sur cette cible.
 */
@Composable
fun MapLocationPreview(
    location: Location,
    pinIcon: Painter,
    pinTint: Color,
    modifier: Modifier = Modifier,
    zoom: Float = DEFAULT_PREVIEW_ZOOM,
    pinSize: Dp = DEFAULT_PIN_SIZE,
    onClick: (() -> Unit)? = null,
) {
    val camera = rememberMapCameraState(initial = MapCameraPosition(location, zoom))
    Box(modifier = modifier) {
        FruxMap(
            camera = camera,
            markers = emptyList(),
            userLocation = null,
            onMarkerClick = {},
            showOrnaments = false,
            modifier = Modifier.fillMaxSize(),
        )
        // Épingle fixe : la pointe vise le centre de la carte, d'où le décalage d'une demi-hauteur
        // (même convention que la mini-carte de choix de position).
        Icon(
            painter = pinIcon,
            contentDescription = null,
            tint = pinTint,
            modifier =
                Modifier
                    .align(Alignment.Center)
                    .size(pinSize)
                    .offset(y = -pinSize / 2),
        )
        // Au-dessus de tout : c'est ce qui lui permet d'intercepter le geste avant la carte.
        Box(
            modifier =
                Modifier
                    .fillMaxSize()
                    .interceptMapGestures(onClick),
        )
    }
}

/**
 * Consomme tout geste dès la passe [PointerEventPass.Initial] et ne transmet qu'un tap simple à
 * [onTap]. Un `Modifier.clickable` ordinaire ne suffirait pas : il écoute en passe Main, une fois
 * le geste déjà servi à la carte (cf. doc de [MapLocationPreview]).
 */
private fun Modifier.interceptMapGestures(onTap: (() -> Unit)?): Modifier =
    pointerInput(onTap) {
        awaitEachGesture {
            awaitFirstDown(requireUnconsumed = false, pass = PointerEventPass.Initial).consume()
            val up = waitForUpOrCancellation(pass = PointerEventPass.Initial)
            up?.consume()
            if (up != null) onTap?.invoke()
        }
    }
